import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Beaker, Droplet, Syringe, Snowflake, Sparkles, AlertTriangle, RotateCcw, CircleAlert, Thermometer, Printer, Save } from "lucide-react";
import { downloadPdf } from "../lib/download";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

/**
 * ReconstitutionWalkthrough
 * Step-by-step "How to reconstitute" guide, opened from the Reconstitution tab.
 * All numbers are derived from the passed vial_mg / bac_ml / dose values so the
 * user sees exact quantities for THIS peptide.
 */
export default function ReconstitutionWalkthrough({ open, onOpenChange, peptide, vialMg, bacMl, drawUnits, onVialSaved }) {
  const [savingVial, setSavingVial] = useState(false);

  if (!peptide) return null;

  const doseLabel = peptide.default_dose != null
    ? `${peptide.default_dose} ${peptide.default_unit || "mcg"}`
    : "your dose";

  const bacSyringeSize = bacMl >= 3 ? "5 mL" : bacMl >= 1.5 ? "3 mL" : "1 mL";

  const canPrint = !!(peptide?.id && vialMg && bacMl && peptide.default_dose != null && ["mg", "mcg"].includes((peptide.default_unit || "").toLowerCase()));
  const canSaveVial = !!(peptide?.name && vialMg && bacMl);

  const onPrint = () => {
    if (!canPrint) return;
    const qs = new URLSearchParams({
      vial_mg: String(vialMg),
      bac_ml: String(bacMl),
      dose: String(peptide.default_dose),
      dose_unit: peptide.default_unit || "mcg",
    }).toString();
    const safe = (peptide.name || "peptide").toLowerCase().replace(/[^a-z0-9-]+/g, "-").replace(/(^-|-$)/g, "");
    downloadPdf(`/peptides/${peptide.id}/protocol.pdf?${qs}`, `protocol-${safe}.pdf`);
  };

  const onSaveVial = async () => {
    if (!canSaveVial || savingVial) return;
    setSavingVial(true);
    // 28-day expiration from today, UTC ISO
    const expiration = new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString();
    try {
      const { data } = await api.post("/vials", {
        peptide_name: peptide.name,
        amount_mg: Number(vialMg),
        bac_water_ml: Number(bacMl),
        purity_pct: 100,
        quantity: 1,
        expiration_date: expiration,
        notes: `Reconstituted from Library walkthrough — ${vialMg} mg + ${bacMl} mL BAC water${drawUnits ? ` → ${drawUnits.toFixed(1)} u per dose` : ""}`,
      });
      toast.success(`Vial added to inventory`, {
        description: `${peptide.name} · ${vialMg} mg / ${bacMl} mL · expires in 28 days`,
        duration: 5000,
      });
      onVialSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingVial(false);
    }
  };

  const steps = [
    {
      icon: Beaker,
      title: "Gather your supplies",
      body: (
        <ul className="list-none space-y-1 text-slate-800">
          <li>• <span className="font-mono-num text-slate-900">{vialMg} mg</span> {peptide.name} lyophilized vial</li>
          <li>• Bacteriostatic (BAC) water — enough for at least <span className="font-mono-num text-slate-900">{bacMl} mL</span></li>
          <li>• One reconstitution syringe ({bacSyringeSize}) with a 21–25 g needle for drawing BAC water</li>
          <li>• U-100 insulin syringe(s) for your daily dose</li>
          <li>• 70% isopropyl alcohol swabs</li>
        </ul>
      ),
    },
    {
      icon: Sparkles,
      title: "Sanitize both vial stoppers",
      body: (
        <p className="text-slate-800">
          Wipe the rubber stopper of the BAC water vial <span className="italic">and</span> the peptide vial with a fresh alcohol swab.
          Let each air-dry for 15–20 seconds before piercing.
        </p>
      ),
    },
    {
      icon: Droplet,
      title: `Draw ${bacMl} mL of BAC water`,
      body: (
        <p className="text-slate-800">
          Insert your reconstitution syringe into the BAC water vial. Invert the vial and slowly withdraw{" "}
          <span className="font-mono-num text-[#0D9488] font-black">{bacMl} mL</span>. Tap the barrel and expel any air bubbles.
        </p>
      ),
    },
    {
      icon: Syringe,
      title: "Inject BAC water into the peptide vial",
      body: (
        <p className="text-slate-800">
          Angle the needle so the water runs down the <span className="italic">inside wall</span> of the vial rather than hitting the powder directly.
          Depress the plunger slowly — this protects the peptide's structure.
        </p>
      ),
    },
    {
      icon: RotateCcw,
      title: "Do NOT shake — gently swirl",
      body: (
        <p className="text-slate-800">
          Roll the vial between your palms or swirl gently until the powder is fully dissolved (usually under 60 seconds).
          Shaking can denature the peptide and reduce potency.
        </p>
      ),
    },
    {
      icon: Thermometer,
      title: "Refrigerate (2–8 °C)",
      body: (
        <p className="text-slate-800">
          Store the reconstituted vial in the fridge, upright, away from light. Most peptides are stable for{" "}
          <span className="font-mono-num text-slate-900">28–30 days</span> once mixed; check the vendor's guidance for exceptions.
        </p>
      ),
    },
    {
      icon: Syringe,
      title: `Draw your dose: ${drawUnits ? `${drawUnits.toFixed(1)} u` : "—"} on a U-100 syringe`,
      body: (
        <div className="text-slate-800 space-y-1.5">
          <p>
            For a {doseLabel} dose, pull the plunger to the{" "}
            <span className="font-mono-num text-[#0D9488] font-black">
              {drawUnits ? `${drawUnits.toFixed(1)} unit${drawUnits === 1 ? "" : "s"}` : "—"}
            </span>{" "}
            mark on a U-100 insulin syringe (1 mL = 100 u).
          </p>
          <p className="text-xs text-slate-500">
            Rotate injection sites (deltoid, abdomen, thigh, glute) to avoid tissue fatigue. Most peptides are subcutaneous —
            follow your clinician's guidance for anything IM.
          </p>
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border border-slate-200 text-slate-900 rounded-none max-w-xl max-h-[92vh] overflow-y-auto" data-testid="recon-walkthrough-dialog">
        <DialogHeader>
          <div className="font-mono-num text-[11px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Protocol Walkthrough</div>
          <DialogTitle className="font-display uppercase tracking-tight text-3xl font-black">
            How to Reconstitute {peptide.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Step-by-step reconstitution protocol for {peptide.name} with the recommended vial size and BAC water volume.
          </DialogDescription>
        </DialogHeader>

        <div className="border border-[#0D9488]/40 bg-[#0D9488]/5 p-3 mt-2">
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 text-[#0D9488] mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-mono-num text-[10px] uppercase tracking-[0.3em] text-[#0D9488] mb-1">// Recipe</div>
              <div className="font-mono-num text-sm text-slate-900">
                <span className="text-[#0D9488] font-black">{vialMg} mg</span> vial
                {" + "}
                <span className="text-[#0D9488] font-black">{bacMl} mL</span> BAC water
                {" → each dose "}
                <span className="text-[#0D9488] font-black">{drawUnits ? `${drawUnits.toFixed(1)} u` : "—"}</span>
              </div>
            </div>
            {canPrint && (
              <button
                type="button"
                onClick={onPrint}
                className="shrink-0 inline-flex items-center gap-1.5 bg-[#0D9488] hover:bg-[#0F766E] text-black font-mono-num text-[10px] uppercase tracking-widest font-black px-3 h-8"
                data-testid="recon-walkthrough-print"
                title="Download a 1-page protocol card PDF"
              >
                <Printer className="w-3.5 h-3.5" /> Print Card
              </button>
            )}
            {canSaveVial && (
              <button
                type="button"
                onClick={onSaveVial}
                disabled={savingVial}
                className="shrink-0 inline-flex items-center gap-1.5 bg-transparent border border-[#0D9488] hover:bg-[#0D9488] hover:text-black text-[#0D9488] font-mono-num text-[10px] uppercase tracking-widest font-black px-3 h-8 disabled:opacity-60"
                data-testid="recon-walkthrough-save-vial"
                title="Add this reconstitution as a new vial in your Inventory (28-day expiration)"
              >
                <Save className="w-3.5 h-3.5" /> {savingVial ? "Saving…" : "Save My Mix"}
              </button>
            )}
          </div>
        </div>

        <ol className="mt-4 space-y-3" data-testid="recon-walkthrough-steps">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={i} className="border border-slate-200 bg-slate-50/40 p-3 flex gap-3" data-testid={`recon-walkthrough-step-${i + 1}`}>
                <div className="shrink-0 flex items-start gap-2">
                  <div className="w-7 h-7 border border-[#0D9488]/40 flex items-center justify-center font-mono-num text-[#0D9488] font-black text-sm">
                    {i + 1}
                  </div>
                  <Icon className="w-4 h-4 text-[#0D9488] mt-1.5 shrink-0" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display uppercase tracking-tight text-slate-900 font-black text-sm mb-1">
                    {s.title}
                  </div>
                  <div className="text-sm leading-relaxed">{s.body}</div>
                </div>
              </li>
            );
          })}
        </ol>

        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 mt-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-[#FF3B30] mt-0.5 shrink-0" />
            <div className="text-xs text-slate-800 leading-relaxed">
              <span className="font-mono-num text-[#FF3B30] uppercase tracking-widest text-[10px]">// Safety</span>
              <p className="mt-1">
                This walkthrough is educational only and is <span className="font-black">not medical advice</span>.
                Always follow your prescribing clinician's protocol. Never reuse needles.
                Sharps go in a designated container — not the trash.
              </p>
            </div>
          </div>
        </div>

        <div className="border border-slate-200 p-3 mt-2">
          <div className="flex items-start gap-2">
            <CircleAlert className="w-4 h-4 text-slate-600 mt-0.5 shrink-0" />
            <div className="text-xs text-slate-600 leading-relaxed">
              <p>
                <span className="font-mono-num text-slate-900 uppercase tracking-widest text-[10px]">// If in doubt</span>
                {" — "}consult the peptide vendor's Certificate of Analysis (CoA) for peptide-specific stability
                and reconstitution guidance. Some peptides (Copper peptides, MOTS-c, GHK-Cu) benefit from a specific
                sterile water or acetic acid diluent — read the label.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
